// Enhanced Role Management Component - Admin only
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

const RoleManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await apiService.get('/roles');
      setRoles(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to fetch roles');
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    navigate('/dashboard/roles/new');
  };

  const handleEditRole = (role) => {
    if (role.name === 'General Staff') {
      setError('Cannot edit General Staff role - it is a system role');
      return;
    }
    navigate(`/dashboard/roles/edit/${role.id}`);
  };

  const handleDelete = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      try {
        await apiService.delete(`/roles/${roleId}`);
        await fetchRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        setError(error.response?.data?.error || 'Failed to delete role');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Roles
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateRole}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          Create Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Roles Grid */}
      <Grid container spacing={3}>
        {roles.map((role) => (
          <Grid item xs={12} md={6} lg={4} key={role.id}>
                          <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: (role.is_system_role || role.name === 'General Staff') ? '2px solid' : '1px solid',
                  borderColor: (role.is_system_role || role.name === 'General Staff') ? 'primary.main' : 'divider',
                  position: 'relative',
                }}
              >
                {(role.is_system_role || role.name === 'General Staff') && (
                  <Chip
                    label={role.name === 'General Staff' ? 'System Role' : 'System Role'}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              
              <CardContent sx={{ flexGrow: 1, pt: (role.is_system_role || role.name === 'General Staff') ? 5 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupIcon color="primary" />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                    {role.name}
                  </Typography>
                </Box>
                
                {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {role.description || 'No description provided'}
                </Typography> */}
                
              </CardContent>
              
              <CardActions sx={{ display: 'flex', justifyContent: 'space-between', p: 2, pt: 0 }}>
              
              <Chip
                  label={role.status}
                  size="small"
                  color={role.status === 'active' ? 'success' : 'default'}
                  variant="outlined"
                />
                  <Tooltip title={role.name === 'General Staff' ? 'Cannot edit system role' : 'Edit Role'}>
                  <span>
                    <IconButton
                      onClick={() => handleEditRole(role)}
                      color="primary"
                      size="small"
                      disabled={role.name === 'General Staff' || role.name === 'Admin'}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                
                {!(role.is_system_role || role.name === 'General Staff') && (
                  <Tooltip title="Delete Role">
                    <IconButton
                      onClick={() => handleDelete(role.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RoleManagement;
